import React, { Component } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { withRouter } from 'react-router-dom'
import { injectIntl, FormattedMessage } from 'react-intl'

import { Container, Content, PageTitle } from 'src/components/Layout'
import { Loader } from 'src/components/Loader'
import {
  hasFeatureFlag,
  logError,
  logMessage,
  getHashFromUrl,
  getKioskDocument,
} from 'src/utils/helpers'

import PushService from 'src/utils/push-service'
import { getAddToWalletUrl, openAddToWalletUrl } from '../helper'

import { Box, Flex } from '@chakra-ui/react'

import SlipCard from '../SlipCard'
import intlMessages from '../translations'

import styles from '../styles.module.less'

class AllSlips extends Component {
  static contextTypes = {
    router: PropTypes.object,
  }

  componentDidMount() {
    const { location, loaded, timeAdded, clearDocument, clearPolicy, isWidgetState } = this.props

    const isForceReload = getHashFromUrl(location).includes('reload')
    if (!loaded || !timeAdded || moment().diff(timeAdded, 'minutes') > 15 || isForceReload) {
      this.loadData()
    }

    this.requestPushPermission()

    // Since LOCATION_CHANGE trick doesn't work for widget, at least to clear it here (when coming back to all documents)
    clearDocument()
    clearPolicy()

    if (isWidgetState) {
      const kioskDocument = getKioskDocument()
      const kioskWidgetContainer = kioskDocument.querySelector('.app-main-content__widget')
      kioskWidgetContainer.scrollTo(0, 0)
    } else {
      const mainContainer =
        window.innerWidth < 900
          ? document.querySelector('.app-main-content')
          : document.querySelector('.app-main-container')
      mainContainer.scrollTo(0, 0)
    }
  }

  /**
   * Request to allow push subscriptions.
   *
   * Will not show "Allow" popup if already allowed.
   *
   * @return {boolean|void}
   */
  requestPushPermission = () => {
    try {
      const { featureFlags, user, isWidgetState } = this.props

      // Request permission to receive push notifications
      if (PushService.isEnabled('documents', user, featureFlags, isWidgetState)) {
        // Request permission to receive push notifications
        PushService.requestPermissionToAllowNotifications(isWidgetState)
      }
    } catch (e) {
      logError('Push Subscription request for documents', e)
    }
  }

  handleBack = () => {
    const { history } = this.props

    history.push('/')
  }

  onAddToWallet = (event, pinkSlip) => {
    try {
      // Prevent opening the pink slip
      event.stopPropagation()

      // TODO: PAT-2042 Use this if we need to show "Add to wallet" button in summary before consent is submitted
      // const { history, setDocument, setAddedToWallet } = this.props

      // if(this.needSlipConsent(pinkSlip)) {
      //
      //   setDocument(pinkSlip)
      //
      //   history.push('/documents/consent/wallet')
      //   return true
      // }

      // Just continue if there is no need for consent
      const walletUrl = getAddToWalletUrl(pinkSlip)
      if (walletUrl) {
        // Mark this pink slip as added, so that we can hide button from summary view
        if (typeof this.props.setAddedToWallet === 'function') {
          this.props.setAddedToWallet(pinkSlip.id)
        }

        openAddToWalletUrl(walletUrl)
      }
    } catch (e) {
      logError('Add to wallet from summary', e)
    }
  }

  handleDocument = document => {
    const { setDocument, history } = this.props

    setDocument(document)

    if (this.needSlipConsent(document)) {
      history.push('/documents/consent/document')
      return true
    }

    // Just continue if there is no need for consent
    history.push('/documents/item')
  }

  needSlipConsent = document => {
    const { hasSlipConsent, countryIsoCode } = this.props

    if (hasSlipConsent === true) {
      return false
    }

    if (document && !(document.type === 'pinkSlips' || document.type === 'fleetSlips')) {
      return false
    }

    return countryIsoCode && countryIsoCode.toLowerCase() === 'ca'
  }

  /**
   * Check if we need to show "Add to Wallet" button for provided slip/document.
   *
   * @param {object} slip
   * @return {boolean}
   */
  isWalletButtonVisible = slip => {
    const { addedToWallet } = this.props

    if (!getAddToWalletUrl(slip)) {
      return false
    }

    // If not already added to wallet and slip consent is not needed
    return !addedToWallet.includes(slip.id) && this.needSlipConsent(slip) === false
  }

  handlePolicyOpen = policy => {
    const { featureFlags, setPolicy, history } = this.props

    // Open full view only if feature flag is received from backend
    if (hasFeatureFlag(featureFlags, 'docs.policies.fullView')) {
      // Set currently open policy
      setPolicy(policy)

      // Redirect to Policy Summary - Full View
      history.push('/documents/policy-summary')
    } else {
      logMessage('Full view not enabled', policy)
    }
  }

  loadData = () => {
    const { featureFlags, getDocuments, getPoliciesDetails } = this.props

    getDocuments()

    // Show policy summaries only if feature flag is received from backend
    if (hasFeatureFlag(featureFlags, 'docs.policies.summary')) {
      getPoliciesDetails()
    }
  }

  renderSlips = allDocuments => {
    if (!(allDocuments && allDocuments.length)) {
      return ''
    }

    // Get Slips
    const slips = allDocuments.filter(
      document =>
        document.hasOwnProperty('type') &&
        (document.type === 'pinkSlips' || document.type === 'fleetSlips'),
    )

    if (!(slips && slips.length)) {
      return ''
    }

    const { intl, language } = this.props

    return (
      <Box>
        <div className={styles.groupHolder}>
          {slips.map((slip, key) => {
            return (
              <SlipCard
                key={key}
                language={language}
                record={slip}
                onSelected={this.handleDocument}
                onAddToWallet={this.onAddToWallet}
                isWalletButtonVisible={this.isWalletButtonVisible(slip)}
                intl={intl}
              />
            )
          })}
        </div>
      </Box>
    )
  }

  render() {
    const { loading, intl, documents, features } = this.props

    const foundedFeature = features.find(f => f.id == 'documents')
    const title =
      foundedFeature && foundedFeature.title
        ? foundedFeature.title
        : intl.formatMessage(intlMessages.documentsHeadline)
    const pinkslips = documents.filter(
      document =>
        document.hasOwnProperty('type') &&
        (document.type === 'pinkSlips' || document.type === 'fleetSlips'),
    )
    return (
      <Flex className='m-documents-my-documents'>
        <Content>
          <PageTitle title={`Pink Slips (${pinkslips.length})`} subtitle={title} />
          <Container className={styles.documentsContainer}>
            {loading ? (
              <Loader styleVariation='staticWithHeight' />
            ) : documents && documents.length ? (
              <div>{this.renderSlips(documents)}</div>
            ) : (
              <h3 className={styles.noResults}>
                <FormattedMessage
                  id='app.documents.noDocument'
                  defaultMessage='No documents found'
                />
              </h3>
            )}
          </Container>
        </Content>
      </Flex>
    )
  }
}

export default withRouter(injectIntl(AllSlips))
